import { UserOutlined } from '@ant-design/icons'
import Page from '@components/Page/Page'
import { TableFilters } from '@components/TableFilters'
import { Venue } from '@components/venue/Venue'
import { Reservation } from '@models/Reservation'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getReservations,
  selectReservations,
  setReservationQuery,
} from '@store/ReservationReducer'
import { selectSelectedUser } from '@store/UserReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { extractSortDataFromSorterResult } from '@supplement-bacon/react-toolkit/utils'
import { SBTable, TableParams } from '@supplement-bacon/sb-table-filters'
import { currencyFormat } from '@utils/currencyFormatter'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import {
  Col,
  DatePicker,
  Spin,
  TableColumnsType,
  TablePaginationConfig,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { SorterResult } from 'antd/es/table/interface'
import dayjs from 'dayjs'
import { useEffect } from 'react'

const { Text } = Typography
const { RangePicker } = DatePicker

const TransactionsTab = () => {
  const dispatch = useAppDispatch()
  const user = useAppSelector(selectSelectedUser())
  const userId: number | undefined = user?.id
  const reservations = useAppSelector(selectReservations())
  const { query, isLoading } = useAppSelector((state) => state.reservation)

  const columns: TableColumnsType<Reservation> = [
    {
      title: 'Venue',
      render: (record: Reservation) => <Venue.Avatar id={record.venue} />,
    },
    {
      title: 'Party size',
      render: (record: Reservation) => (
        <Tag color="purple" icon={<UserOutlined />}>
          {record.guests}
        </Tag>
      ),
    },
    {
      title: 'Date',
      key: 'date',
      render: (record: Reservation) => (
        <Text style={{ whiteSpace: 'nowrap' }}>
          {dayjs(record.date).format('DD-MM-YYYY HH:mm')}
        </Text>
      ),
    },
    {
      title: 'Reference',
      render: (record: Reservation) => (
        <Tooltip title={record.reference}>
          <Tag variant="outlined" color="blue">
            <Text copyable={{ text: record.reference.toString() }}>
              {record.reference?.toString?.()}{' '}
            </Text>
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Seating area',
      render: (record: Reservation) => <Tag>{record.seatingArea?.en.name}</Tag>,
    },
    {
      title: 'Status',
      render: (record: Reservation) => (
        <Tag variant="outlined">{record.status}</Tag>
      ),
    },
    {
      title: 'Spend',
      render: (record: Reservation) =>
        record.financial ? (
          <Text>{currencyFormat(record.financial.amount, true)}</Text>
        ) : (
          '--'
        ),
    },
    {
      title: 'Source',
      render: (record: Reservation) => (
        <Tag variant="outlined" style={{ textTransform: 'capitalize' }}>
          {record.bookedByProduct}
        </Tag>
      ),
    },
  ]

  const handleTableChange = (
    pagination: TablePaginationConfig,
    sorter: SorterResult<Reservation> | SorterResult<Reservation>[]
  ) => {
    const newQuery: TableParams = {
      ...query,
      pagination,
      filters: {
        ...query.filters,
        users: user ? [user.id] : [],
      },
      ...extractSortDataFromSorterResult(sorter),
      // Resetting the columnKey and order to default for sorting
      // This ensures the table sorts by date in descending order
      // Later we can add more sorting options if needed, but atm this is sufficient
      columnKey: 'date',
      order: 'descend',
    }
    dispatch(setReservationQuery(newQuery))
    dispatch(getReservations(newQuery))
  }

  useEffect(() => {
    if (!userId) return
    const newQuery: TableParams = {
      ...query,
      pagination: { ...query.pagination, current: 1 },
      filters: {
        ...query.filters,
        users: userId ? [userId] : [],
      },
      // This tab renders the Seating area but not the customer, so request
      // seatingArea only (the query slice is shared with the Transactions page,
      // which also opts into user).
      with: ['seatingArea'],
      columnKey: 'date',
      order: 'descend',
    }
    dispatch(setReservationQuery(newQuery))
    dispatch(getReservations(newQuery))
  }, [userId])

  if (!user) {
    return <Spin />
  }

  // The table is GET /reservations: `list reservation`, not `read user`. The
  // route gate on /users/:userId/transactions is what actually keeps this page
  // from mounting; this only keeps the page's own claim honest.
  return (
    <Page permissions={Permission.listReservation}>
      <Col span={24}>
        <TableFilters
          onValuesChange={(filters, search) => {
            const newQuery: TableParams = {
              ...query,
              filters: {
                ...query.filters,
                ...filters,
                ...(filters.dateRange?.[0] && filters.dateRange?.[1]
                  ? {
                      startDate: [
                        dayjs(String(filters.dateRange?.[0])).format(
                          'YYYY-MM-DD'
                        ),
                      ],
                      endDate: [
                        dayjs(String(filters.dateRange?.[1])).format(
                          'YYYY-MM-DD'
                        ),
                      ],
                    }
                  : {}),
              },
              search: { ...query.search, ...search },
            }
            dispatch(setReservationQuery(newQuery))
            dispatch(getReservations(newQuery))
          }}
          left={[
            {
              name: 'venues',
              type: 'filter',
              input: (
                <SBAsyncSelect
                  style={{ minWidth: '300px' }}
                  multiple
                  selectProps={{
                    maxTagCount: 'responsive',
                  }}
                  placeholder="Select venues"
                  entity={Entity.venue}
                  searchFieldName="name"
                  allowClear
                />
              ),
            },
          ]}
          right={[
            {
              name: 'dateRange',
              type: 'filter',
              input: <RangePicker className="w-full" placement="bottomRight" />,
            },
          ]}
        />
      </Col>
      <Col span={24}>
        <SBTable
          columns={columns}
          dataSource={reservations}
          loading={isLoading}
          onChange={handleTableChange}
          pagination={query.pagination}
          scroll={{ x: 'max-content' }}
        />
      </Col>
    </Page>
  )
}

export default TransactionsTab
